/**
 * Display metadata for the rail's provider well, keyed by the credential's
 * `provider` string. Anything the API returns that is not listed here still
 * gets a row, with the kit's fallback mark and a neutral tint, so a new vendor
 * appears the day its credential does rather than the day someone edits this.
 */
const PROVIDER_META: Record<string, { name: string; bgClass: string }> = {
  aws: { name: "AWS", bgClass: "bg-aws/10" },
  azure: { name: "Azure", bgClass: "bg-azure/10" },
  gcp: { name: "GCP", bgClass: "bg-gcp/10" },
  openai: { name: "OpenAI", bgClass: "bg-openai/10" },
  anthropic: { name: "Anthropic", bgClass: "bg-anthropic/10" },
  openrouter: { name: "OpenRouter", bgClass: "bg-openrouter/10" },
  cursor: { name: "Cursor", bgClass: "bg-muted" },
};
const PROVIDER_ORDER = Object.keys(PROVIDER_META);

export interface ConnectedProvider {
  id: string;
  name: string;
  bgClass: string;
  /** Credentials on this provider inside the selected scope. */
  count: number;
}

/**
 * Fold a credentials list into one row per provider with its account count,
 * clouds first in the product's usual order, then AI vendors, then anything
 * unrecognised alphabetically. Pure, so the test for it needs no rail.
 */
export function connectedProviders(
  credentials: ReadonlyArray<{ provider: string }>,
): ConnectedProvider[] {
  const counts = new Map<string, number>();
  for (const credential of credentials) {
    const id = (credential.provider || "").toLowerCase();
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const rank = (id: string) => {
    const i = PROVIDER_ORDER.indexOf(id);
    return i < 0 ? PROVIDER_ORDER.length : i;
  };
  return [...counts.entries()]
    .map(([id, count]) => ({
      id,
      count,
      name: PROVIDER_META[id]?.name ?? id,
      bgClass: PROVIDER_META[id]?.bgClass ?? "bg-muted",
    }))
    .sort((a, b) => rank(a.id) - rank(b.id) || a.name.localeCompare(b.name));
}
